import type { Editor } from '@tiptap/core';
import { useEditorState } from '@tiptap/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faIndent, faOutdent } from '@fortawesome/free-solid-svg-icons';
import type { CSSProperties, MouseEvent, ReactNode } from 'react';

import { RichTextFontColorPicker } from './RichTextFontColorPicker';
import { RichTextFontSizeDropdown } from './RichTextFontSizeDropdown';
import { RichTextLinkDialog } from './RichTextLinkDialog';

interface RichTextToolbarButtonProps {
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  title: string;
  children: ReactNode;
  buttonStyle?: CSSProperties;
  activeButtonStyle?: CSSProperties;
}

export function RichTextToolbarButton({
  onClick,
  isActive,
  disabled = false,
  title,
  children,
  buttonStyle,
  activeButtonStyle,
}: RichTextToolbarButtonProps) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        ...(buttonStyle ?? {}),
        ...(isActive ? (activeButtonStyle ?? {}) : {}),
        ...(disabled ? { opacity: 0.4, cursor: 'default' } : {}),
      }}
      title={title}
      type="button"
    >
      {children}
    </button>
  );
}

export function preventRichTextToolbarMouseDown(event: MouseEvent<HTMLElement>) {
  event.preventDefault();
}

export type RichTextToolbarActionId =
  | 'bold'
  | 'italic'
  | 'underline'
  | 'strike'
  | 'bulletList'
  | 'orderedList'
  | 'indent'
  | 'outdent'
  | 'horizontalRule'
  | 'link'
  | 'blockquote'
  | 'clearFormatting';

export type RichTextToolbarItem =
  | {
      kind: 'action';
      action: RichTextToolbarActionId;
      title?: string;
      label?: string;
    }
  | {
      kind: 'separator';
    }
  | {
      kind: 'fontSizeDropdown';
    }
  | {
      kind: 'fontColorPicker';
      backgroundColors: string[];
    };

function getRichTextToolbarActionTitle(
  action: RichTextToolbarActionId,
  title?: string
): string {
  if (title) {
    return title;
  }

  switch (action) {
    case 'bold':
      return 'Bold (Ctrl+B)';
    case 'italic':
      return 'Italic (Ctrl+I)';
    case 'underline':
      return 'Underline (Ctrl+U)';
    case 'strike':
      return 'Strikethrough';
    case 'bulletList':
      return 'Bulleted list';
    case 'orderedList':
      return 'Numbered list';
    case 'indent':
      return 'Indent';
    case 'outdent':
      return 'Outdent';
    case 'horizontalRule':
      return 'Horizontal rule';
    case 'link':
      return 'Insert link';
    case 'blockquote':
      return 'Blockquote';
    case 'clearFormatting':
      return 'Clear formatting';
  }
}

function getRichTextToolbarActionLabel(
  action: RichTextToolbarActionId,
  label?: string
): ReactNode {
  if (label) {
    return label;
  }

  switch (action) {
    case 'bold':
      return <strong>B</strong>;
    case 'italic':
      return <em>I</em>;
    case 'underline':
      return <u>U</u>;
    case 'strike':
      return <s>S</s>;
    case 'bulletList':
      return '*';
    case 'orderedList':
      return '1.';
    case 'indent':
      return <FontAwesomeIcon icon={faIndent} />;
    case 'outdent':
      return <FontAwesomeIcon icon={faOutdent} />;
    case 'horizontalRule':
      return '—';
    case 'link':
      return '🔗';
    case 'blockquote':
      return '❝';
    case 'clearFormatting':
      return 'T̸';
  }
}

function isRichTextToolbarActionActive(
  editor: Editor,
  action: RichTextToolbarActionId,
  forceActive?: Partial<Record<RichTextToolbarActionId, boolean>>
): boolean {
  if (forceActive?.[action] === true) {
    return true;
  }

  switch (action) {
    case 'bold':
      return editor.isActive('bold');
    case 'italic':
      return editor.isActive('italic');
    case 'underline':
      return editor.isActive('underline');
    case 'strike':
      return editor.isActive('strike');
    case 'bulletList':
      return editor.isActive('bulletList');
    case 'orderedList':
      return editor.isActive('orderedList');
    case 'link':
      return editor.isActive('link');
    case 'blockquote':
      return editor.isActive('blockquote');
    case 'horizontalRule':
    case 'clearFormatting':
    case 'indent':
    case 'outdent':
      return false;
  }
}

function runRichTextToolbarAction(
  editor: Editor,
  action: RichTextToolbarActionId
): void {
  switch (action) {
    case 'bold':
      editor.chain().focus().toggleBold().run();
      return;
    case 'italic':
      editor.chain().focus().toggleItalic().run();
      return;
    case 'underline':
      editor.chain().focus().toggleUnderline().run();
      return;
    case 'strike':
      editor.chain().focus().toggleStrike().run();
      return;
    case 'bulletList':
      editor.chain().focus().toggleBulletList().run();
      return;
    case 'orderedList':
      editor.chain().focus().toggleOrderedList().run();
      return;
    case 'indent':
      editor.chain().focus().sinkListItem('listItem').run();
      return;
    case 'outdent':
      editor.chain().focus().liftListItem('listItem').run();
      return;
    case 'horizontalRule':
      editor.chain().focus().setHorizontalRule().run();
      return;
    case 'blockquote':
      editor.chain().focus().toggleBlockquote().run();
      return;
    case 'clearFormatting': {
      const chain = editor.chain().focus();
      // Lift out of list items first if inside a list
      if (editor.isActive('bulletList') || editor.isActive('orderedList')) {
        chain.liftListItem('listItem');
      }
      chain.clearNodes().unsetAllMarks().run();
      return;
    }
    case 'link': {
      const url = window.prompt('Enter URL:')?.trim();
      if (url) {
        editor.chain().focus().setLink({ href: url }).run();
      }
      return;
    }
  }
}

interface RichTextToolbarProps {
  editor: Editor;
  items: RichTextToolbarItem[];
  toolbarStyle?: CSSProperties;
  buttonStyle?: CSSProperties;
  activeButtonStyle?: CSSProperties;
  dividerStyle?: CSSProperties;
  selectStyle?: CSSProperties;
  forceActive?: Partial<Record<RichTextToolbarActionId, boolean>>;
}

export function RichTextToolbar({
  editor,
  items,
  toolbarStyle,
  buttonStyle,
  activeButtonStyle,
  dividerStyle,
  selectStyle,
  forceActive,
}: RichTextToolbarProps): ReactNode {
  const listActions = useEditorState({
    editor,
    selector: ({ editor: currentEditor }) => ({
      canIndent: Boolean(currentEditor.schema.nodes['listItem'])
        && currentEditor.can().sinkListItem('listItem'),
      canOutdent: Boolean(currentEditor.schema.nodes['listItem'])
        && currentEditor.can().liftListItem('listItem'),
    }),
  });
  const fallbackDividerStyle: CSSProperties = {
    width: '1px',
    height: '20px',
    backgroundColor: 'rgba(0, 0, 0, 0.12)',
    margin: '0 4px',
  };

  return (
    <div style={toolbarStyle} onMouseDown={preventRichTextToolbarMouseDown}>
      {items.map((item, index) => {
        if (item.kind === 'separator') {
          return (
            <div
              key={`separator-${index}`}
              style={dividerStyle ?? fallbackDividerStyle}
              aria-hidden="true"
            />
          );
        }

        if (item.kind === 'fontSizeDropdown') {
          return (
            <RichTextFontSizeDropdown
              key="fontSize"
              editor={editor}
              selectStyle={selectStyle}
            />
          );
        }

        if (item.kind === 'fontColorPicker') {
          return (
            <RichTextFontColorPicker
              key="fontColor"
              editor={editor}
              backgroundColors={item.backgroundColors}
              buttonStyle={buttonStyle}
              activeButtonStyle={activeButtonStyle}
            />
          );
        }

        if (item.action === 'link') {
          return (
            <RichTextLinkDialog
              key="link"
              editor={editor}
              buttonStyle={buttonStyle}
              activeButtonStyle={activeButtonStyle}
            />
          );
        }

        return (
          <RichTextToolbarButton
            key={`${item.action}-${index}`}
            onClick={() => runRichTextToolbarAction(editor, item.action)}
            isActive={isRichTextToolbarActionActive(editor, item.action, forceActive)}
            disabled={item.action === 'indent' ? !listActions.canIndent
              : item.action === 'outdent' ? !listActions.canOutdent : false}
            title={getRichTextToolbarActionTitle(item.action, item.title)}
            buttonStyle={buttonStyle}
            activeButtonStyle={activeButtonStyle}
          >
            {getRichTextToolbarActionLabel(item.action, item.label)}
          </RichTextToolbarButton>
        );
      })}
    </div>
  );
}
